import { modelToUI, uiToModel } from '~/components/DrawingCanvas/util/units'
import { MetadataFields } from '~/components/MetadataFields'
import { PanelSection } from '~/components/UIKit/Panel'
import { Panel } from '~/components/UIKit/Panel/Panel'
import ObjectClassName from '~/config/objectClassNames'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { updateElevationLine } from '~/store/objects'
import { ElevationLine, ElevationPoint } from '~/store/objects/types'
import { SELECTED_ELEVATION_LINE_PANEL } from '~/store/panel/types'
import { mostRecentSelectedObjectOfClassName } from '~/store/selectedObjects/selectors'
import { Alert } from '~/ui/Alert'
import { DimensionInput } from '~/ui/DimensionInput'

const EPSILON = 0.1

const getElevationLineHeight = (
  elevationLine: ElevationLine,
  elevationPoints: Record<string, ElevationPoint>
): number | { error: string } => {
  const { elevationPointIds } = elevationLine
  const [point1, point2] = [elevationPoints[elevationPointIds[0]], elevationPoints[elevationPointIds[1]]]
  if (!point1 || !point2) return { error: 'Elevation line is missing point(s)' }
  const isApproximatelySameHeight = Math.abs(point1.position.z - point2.position.z) < EPSILON
  return isApproximatelySameHeight ? uiToModel(point1.position.z) : { error: 'Selected points have differing heights' }
}

const SelectedElevationLinePanel = () => {
  const isTouchUI = useAppSelector(state => state.userInterface.isTouchUI)
  const isLocked = useAppSelector(state => state.layers.layers.ELEVATION_LINE.locked)
  const elevationPoints = useAppSelector(state => state.objects.present.elevationPoints)
  const selectedElevationLine = useAppSelector(({ selectedObjects, objects }) =>
    mostRecentSelectedObjectOfClassName(ObjectClassName.ELEVATION_LINE, { selectedObjects, objects })
  )
  const lineHeight = selectedElevationLine ? getElevationLineHeight(selectedElevationLine, elevationPoints) : null
  const isError = lineHeight === null || typeof lineHeight !== 'number'

  const dispatch = useAppDispatch()

  const handleUpdateElevationLine = (newValue: number) => {
    if (!selectedElevationLine) return
    dispatch(
      updateElevationLine({
        elevationLine: {
          ...selectedElevationLine,
          height: modelToUI(newValue),
        },
      })
    )
  }

  return (
    <Panel
      title="Elevation Line"
      alignment="right"
      docked
      panelKey={SELECTED_ELEVATION_LINE_PANEL}
      hasToolbar={isTouchUI}
    >
      {selectedElevationLine ? (
        <PanelSection>
          <div className="flex flex-col gap-3">
            <DimensionInput
              isDisabled={isLocked || isError}
              onCommit={handleUpdateElevationLine}
              value={typeof lineHeight === 'number' ? lineHeight : NaN}
              placeholder="N/A"
              label="Height"
            />
            {!!lineHeight && typeof lineHeight !== 'number' && <Alert>{lineHeight.error}</Alert>}
          </div>
          <MetadataFields
            onChange={(fieldName, newValue) => {
              dispatch(
                updateElevationLine({
                  elevationLine: {
                    ...selectedElevationLine,
                    metadata: { ...selectedElevationLine.metadata, [fieldName]: newValue },
                  },
                })
              )
            }}
            onDeleteImage={elevationLine => dispatch(updateElevationLine({ elevationLine }))}
            selectedObjects={[selectedElevationLine]}
          />
        </PanelSection>
      ) : null}
    </Panel>
  )
}

export default SelectedElevationLinePanel
